/**
 * TradingLayout Component
 *
 * Full-screen layout container for trading phases with compact session header strip.
 * Provides consistent structure across all TradingHub phases.
 */

// 1. External dependencies
import React from 'react';

// 2. Internal absolute imports
import { Card, CardBody, CardFooter } from '@enfomentis/ui';
import { SESSION_TYPE_LABELS, STATUS_LABELS } from '@/constants/tradingSession';

// 3. Type imports
import type { SessionDraft } from '@/types/transient/SessionDraft';

// ===== COMPONENT =====

export interface TradingLayoutProps {
  /** Type of trading session (live, paper, observation, replay) */
  sessionType: SessionDraft['sessionType'];
  /** Current session status — displayed as phase status indicator */
  sessionStatus: SessionDraft['status'];
  /** Action menu triggers for the header right zone (rendered as-is) */
  actionMenuSlot?: React.ReactNode;
  /** Clock/timer slot for the header left zone — T08 populates this */
  clockTimerSlot?: React.ReactNode;
  /** Phase content */
  children: React.ReactNode;
  /**
   * Footer content. Controls footer rendering:
   * - `null`: hides footer entirely
   * - `ReactNode`: renders custom footer content
   * - `undefined` (not provided): hides footer (no default)
   */
  footer?: React.ReactNode | null;
}

/**
 * Trading Layout Container
 *
 * Provides:
 * - Compact session header strip (session type, phase status, action menu slots)
 * - Full-screen card layout
 * - Optional footer for custom content
 * - Consistent structure for all trading phases
 */
export const TradingLayout: React.FC<TradingLayoutProps> = ({
  sessionType,
  sessionStatus,
  actionMenuSlot,
  clockTimerSlot,
  children,
  footer
}) => {
  return (
    <Card
      radius="none"
      shadow="none"
      className="h-full w-full flex flex-col border-0"
    >
      {/* Session Header Strip */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-default-200 shrink-0">
        {/* Left Zone — session identity + clock/timer */}
        <div className="flex items-center gap-4 min-w-0">
          {/* Desktop: full labels */}
          <span className="hidden md:inline text-xs text-text-secondary truncate">
            Type: <span className="text-text-primary font-medium">{SESSION_TYPE_LABELS[sessionType]}</span>
          </span>
          <span className="hidden md:inline text-xs text-text-secondary">
            Phase: <span className="text-text-primary font-medium">{STATUS_LABELS[sessionStatus]}</span>
          </span>
          {/* Mobile: compact single token */}
          <span className="md:hidden text-xs text-text-primary font-medium truncate">
            {SESSION_TYPE_LABELS[sessionType]} &middot; {STATUS_LABELS[sessionStatus]}
          </span>
          {clockTimerSlot}
        </div>

        {/* Right Zone — action menu triggers */}
        <div className="flex items-center gap-1 shrink-0">
          {actionMenuSlot}
        </div>
      </div>

      {/* Main Content Area */}
      <CardBody className="flex-1 p-0 min-h-0">
        {children}
      </CardBody>

      {/* Footer — null/undefined: hidden, ReactNode: custom content */}
      {footer != null && (
        <CardFooter className="justify-center border-t border-default-200">
          {footer}
        </CardFooter>
      )}
    </Card>
  );
};
